import { Component, OnInit, inject, DestroyRef, signal, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgIf, DatePipe } from '@angular/common';
import { HttpParams, HttpContext } from '@angular/common/http';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { interval } from 'rxjs';
import { DateTime } from 'luxon';

import { OccurrenceService } from '../../service/occurrence.service';
import { UpcomingService } from '../../service/upcoming.service';
import { Occurrence } from '../../model/occurrence.model';
import { SKIP_LOADING } from '../../interceptors/loading.interceptor';

/**
 * Composant modal pour afficher le prochain cours imminent
 * Affiche une notification quand un cours Teams est sur le point de commencer
 * Version moderne avec signals et Control Flow syntax
 */
@Component({
  selector: 'app-next-session-modal',
  templateUrl: './next-session-modal.component.html',
  styleUrls: ['./next-session-modal.component.scss'],
  standalone: true,
  imports: [NgIf, DatePipe, TranslateModule, MatButtonModule, MatDividerModule]
})
export class NextSessionModalComponent implements OnInit {
  // Services injectés
  private upcomingService = inject(UpcomingService);
  private occurrenceService = inject(OccurrenceService);
  private destroyRef = inject(DestroyRef);

  // State avec signals
  nextSession = signal<Occurrence | null>(null);
  timeToGoInClass = signal<boolean>(false);
  isValidProvider = signal<boolean>(false);

  // Computed signals
  shouldShowModal = computed(() => 
    this.timeToGoInClass() && this.nextSession() !== null
  );

  ngOnInit(): void {
    // Charger la prochaine session
    this.getNextSession();

    // Vérifier toutes les secondes si c'est l'heure d'aller en cours
    interval(1000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.checkTimeToGoInClass());

    // Rafraîchir les données toutes les 30 secondes
    interval(34000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.getNextSession());
  }

  /**
   * Vérifie si c'est l'heure d'aller en cours
   * Conditions :
   * - Formation active
   * - Provider Teams
   * - Heure entre start et end
   * - Warning non affiché
   */
  private checkTimeToGoInClass(): void {
    const session = this.nextSession();
    
    if (!session || session.formation_status !== 'active') {
      this.timeToGoInClass.set(false);
      return;
    }

    // Uniquement pour Teams
    if (session.student.user.video_provider !== 'teams') {
      this.timeToGoInClass.set(false);
      return;
    }

    // Si le warning a déjà été affiché
    if (session.is_student_class_warning_showed) {
      this.timeToGoInClass.set(false);
      return;
    }

    // Vérifier si on est dans la plage horaire du cours
    const now = DateTime.now();
    const start = DateTime.fromJSDate(new Date(session.start));
    const end = DateTime.fromJSDate(new Date(session.end));


    if (now >= start && now <= end) {
      this.timeToGoInClass.set(true);
      this.checkProviderRequirements();
    } else {
      this.timeToGoInClass.set(false);
    }
  }

  /**
   * Vérifie que le lien Teams est bien disponible
   */
  private checkProviderRequirements(): void {
    const session = this.nextSession();
    this.isValidProvider.set(!!session?.provider_link);
  }

  /**
   * Charge le prochain cours à venir
   */
  private getNextSession(): void {
    const params = new HttpParams()
      .set('page_size', '1')
      .set('salt', Math.random().toString()); // Cache busting

    // Créer un contexte HTTP pour désactiver le loading (polling toutes les 30s)
    const context = new HttpContext().set(SKIP_LOADING, true);

    this.upcomingService.getUpcomings(params, context)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((upcomings: any) => {
        
        // L'API retourne un objet paginé { results: [], count, next, previous }
        if (upcomings?.results && upcomings.results.length > 0) {
          this.nextSession.set(upcomings.results[0]);
        } else {
          this.nextSession.set(null);
        }
      });
  }

  /**
   * Rejoindre le cours Teams
   */
  joinClass(): void {
    this.patchData('is_student_provider_link_clicked');
    this.openTeamsLink();
    this.close();
  }

  /**
   * Mettre à jour une donnée de l'occurrence
   */
  private patchData(field: string): void {
    const session = this.nextSession();
    if (!session) return;

    const params = {
      [field]: DateTime.now().toISO(),
    };

    this.occurrenceService.patchObservableOccurrence(session.url, params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(occurrence => {
        if (occurrence) {
          this.nextSession.set(occurrence);
        }
      });
  }

  /**
   * Fermer la modal
   */
  close(): void {
    this.timeToGoInClass.set(false);
    this.patchData('is_student_class_warning_showed');
  }

  /**
   * Ouvrir le lien Teams dans un nouvel onglet
   */
  private openTeamsLink(): void {
    const session = this.nextSession();
    if (session?.provider_link) {
      window.open(session.provider_link, '_blank');
    }
  }
}
